import { Component, Element, h, Host, Prop, State, Watch } from '@stencil/core';
import { ChartConfiguration } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

import {
  LineChartGridOptions,
  LineChartMarkerPointOptions,
  LineChartXAxisOptions,
  LineChartYAxisOptions,
} from '../../../services/interfaces';
import { ChartJsService } from '../../../services/libraries';
import { getCSSVariableValue, hexToRGB, parseProp, parsePropToArray } from '../../../services/utils';

let lineChartId = 0;
export interface PkLineChartLabel {
  value: string | number;
  label: string;
}

@Component({
  tag: 'pk-line-chart',
  styleUrl: './scss/pk-line-chart.scss',
  shadow: true,
})
export class PkLineChart {
  // Own Properties
  // ====================

  private chart: ChartJsService;
  private canvasElemRef: HTMLCanvasElement;
  private hostShadowRoot: Element;
  private lineChartId = `line-chart-${++lineChartId}`;

  // Reference to host HTML element
  // ===================================

  @Element() el: HTMLPkLineChartElement;

  // State() variables
  // Inlined decorator, alphabetical order
  // =======================================

  @State() chartConfig: ChartConfiguration;
  @State() chartData: number[];
  @State() chartLabels: string[];

  // Public Property API
  // ========================

  /** Specify the aria label text for accessibility */
  @Prop({ reflect: true }) ariaLabel: string | null = 'Panenka line chart';

  /** Positon of the X axis on the chart ('none' will hide the labels) */
  @Prop({ reflect: true }) axisXDisplay: LineChartXAxisOptions = 'default';

  /** Positon of the Y axis on the chart (left or right side) */
  @Prop({ reflect: true }) axisYDisplay: LineChartYAxisOptions = 'default';

  /** The series values for the chart. The series property expects a single array of numbers */
  @Prop({ reflect: true }) data: string | number[];

  /** If true, the gradient fill won't be applied below the line chart */
  @Prop({ reflect: true }) disableGradientFill: boolean = false;

  /** If true, the gradient stroke won't be applied on the line chart */
  @Prop({ reflect: true }) disableGradientStroke: boolean = false;

  /** The width of the border line */
  @Prop({ reflect: true }) gridBorderWidth: number = 0;

  /** Stroke width of grid lines */
  @Prop({ reflect: true }) gridLineWidth: number = 2;

  /** Width of the tick mark in pixels */
  @Prop({ reflect: true }) gridTickWidth: number = 0;

  /** It will set the height of the SVG chart (only the circle) */
  @Prop({ reflect: true }) height: number = 250;

  /** If true, the inline graph data won't be shown */
  @Prop({ reflect: true }) hideInlineGraphData: boolean = false;

  /** Labels for each serie value of the chart. The labels property expects a single array */
  @Prop({ reflect: true }) labels: string | string[];

  /** Line stroke width */
  @Prop({ reflect: true }) lineStroke: number = 3;

  /** It sets how the lines grid are displayed (`default`: only vertical lines) */
  @Prop({ reflect: true }) lineGrid: LineChartGridOptions = 'default';

  /** Whether to display or not the marker points in the line of the chart */
  @Prop({ reflect: true }) markerPoint: LineChartMarkerPointOptions = 'default';

  /** If true, it will reverse the scale values on the Y axis (the biggest value on the bottom and smallest value on the top) */
  @Prop({ reflect: true }) reverseYAxis: boolean = false;
  /** User-defined fixed step size for the scale Y. */
  @Prop({ reflect: true }) stepSizeYAxis?: number = null;

  /** User defined minimum number for the scale, overrides minimum value from data.*/
  @Prop({ reflect: true }) minYAxis?: number = null;

  /** User defined maximum number for the scale, overrides maximum value from data.*/
  @Prop({ reflect: true }) maxYAxis?: number = null;

  /** Custom Y labels to apply if they matches, otherwise it return the tick values */
  @Prop({ reflect: true }) customYLabels?: string | PkLineChartLabel[];

  /** Custom X labels to apply if they matches, otherwise it return the tick values */
  @Prop({ reflect: true }) customXLabels?: string | PkLineChartLabel[];

  // Prop lifecycle events
  // =======================

  @Watch('data')
  updateSeries() {
    this.chartData = parsePropToArray(this.data);
    this.chartConfig.data.datasets[0].data = this.chartData;
  }

  @Watch('labels')
  updateLabels() {
    this.chartLabels = parsePropToArray(this.labels);
    this.chartConfig.data.labels = this.chartLabels;
  }
  @Watch('data')
  @Watch('labels')
  onDataLabelChange() {
    if (!this.chart) return;

    this.chart.update(this.chartConfig);
  }

  @Watch('axisXDisplay')
  @Watch('axisYDisplay')
  @Watch('disableGradientFill')
  @Watch('disableGradientStroke')
  @Watch('gridBorderWidth')
  @Watch('gridLineWidth')
  @Watch('gridTickWidth')
  @Watch('height')
  @Watch('hideInlineGraphData')
  @Watch('lineGrid')
  @Watch('lineStroke')
  @Watch('markerPoint')
  @Watch('reverseYAxis')
  @Watch('stepSizeYAxis')
  @Watch('minYAxis')
  @Watch('maxYAxis')
  @Watch('customYLabels')
  @Watch('customXLabels')
  onConfigChange() {
    if (!this.chart) return;

    this.chart.destroy();
    this.initChart();
  }

  // Events section
  // Requires JSDocs for public API documentation
  // ==============================================

  // Component lifecycle events
  // Ordered by their natural call order
  // =====================================

  componentWillLoad() {
    this.chartData = parsePropToArray(this.data);
    this.chartLabels = parsePropToArray(this.labels);
  }

  componentDidLoad() {
    this.hostShadowRoot = this.el.shadowRoot.host;
    this.initChart();
  }

  // Listeners
  // ==============

  // Public methods API
  // These methods are exposed on the host element.
  // Always use two lines.
  // Public Methods must be async.
  // Requires JSDocs for public API documentation.
  // ===============================================

  // Local methods
  // Internal business logic.
  // These methods cannot be called from the host element.
  // =======================================================

  private setChartConfig = () => {
    if (!this.hostShadowRoot) return;
    const labelsY = this.customYLabels ? parseProp<PkLineChartLabel[]>(this.customYLabels) : null;
    const labelsX = this.customXLabels ? parseProp<PkLineChartLabel[]>(this.customXLabels) : null;

    this.chartConfig = {
      type: 'line',
      data: {
        labels: this.chartLabels,
        datasets: [
          {
            clip: false,
            data: this.chartData,
            ...this.datasetsCommonConfig(),
          },
        ],
      },
      plugins: [ChartDataLabels],
      options: {
        animation: {
          easing: 'easeInOutBack',
          duration: 2000,
        },
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: {
            top: 40,
          },
        },
        plugins: {
          // Datalabels configuration options:
          // https://chartjs-plugin-datalabels.netlify.app/guide/
          datalabels: {
            align: 'end',
            anchor: 'end',
            color: this.cssValue('line-chart-datalabel--color'),
            display: !this.hideInlineGraphData,
            font: {
              family: this.cssValue('line-chart--font-family'),
              size: this.cssValue2Int('line-chart-datalabel--font-size'),
            },
            offset: 4,
          },
          tooltip: {
            enabled: false,
          },
        },
        scales: {
          x: {
            display: this.axisXDisplay !== LineChartXAxisOptions[1],
            grid: {
              display: this.lineGrid === LineChartGridOptions[0] || this.lineGrid === LineChartGridOptions[2],
              ...this.scalesCommonConfig(),
            },
            ticks: {
              ...this.scalesTicksCommonConfig(),
              ...(labelsX && {
                callback: (tickValue) => {
                  const val = labelsX?.find((l) => l.value === tickValue);
                  return val?.label ?? tickValue;
                },
              }),
            },
            ...this.borderConfig,
          },
          y: {
            beginAtZero: this.reverseYAxis,
            display: this.axisYDisplay !== LineChartYAxisOptions[0],
            ...(!isNaN(this.minYAxis) && { min: this.minYAxis }),
            ...(!isNaN(this.maxYAxis) && { max: this.maxYAxis }),
            grid: {
              display: this.lineGrid === LineChartGridOptions[1] || this.lineGrid === LineChartGridOptions[2],
              ...this.scalesCommonConfig(),
            },
            position: this.axisYDisplay as 'left' | 'right',
            reverse: this.reverseYAxis,
            ticks: {
              ...this.scalesTicksCommonConfig(),
              ...(!isNaN(this.stepSizeYAxis) && { stepSize: this.stepSizeYAxis }),
              ...(labelsY && {
                callback: (tickValue) => {
                  const val = labelsY?.find((l) => l.value === tickValue);
                  return val?.label ?? tickValue;
                },
              }),
            },
            ...this.borderConfig,
          },
        },
      },
    };
  };

  private initChart = () => {
    this.setChartConfig();
    this.chart = new ChartJsService(this.canvasElemRef, this.chartConfig);
  };

  private datasetsCommonConfig = () => {
    if (!this.canvasElemRef) return;

    const ctx = this.canvasElemRef.getContext('2d');
    // Create a linear gradient
    // The start gradient point is at x=500, y=0
    // The end gradient point is at x=100, y=0
    const gradient = { x0: 900, y0: 0, x1: 100, y1: 0 };
    const gradientStroke = ctx.createLinearGradient(gradient.x0, gradient.y0, gradient.x1, gradient.y1);

    gradientStroke.addColorStop(0, this.cssValue('line-chart--gradient-stroke-start'));
    gradientStroke.addColorStop(1, this.cssValue('line-chart--gradient-stroke-end'));

    const opacity = 0.1;
    const gradientFill = ctx.createLinearGradient(gradient.x0, gradient.y0, gradient.x1, gradient.y1);
    gradientFill.addColorStop(0, `rgba(${hexToRGB(this.cssValue('line-chart--gradient-fill-start'))}, ${opacity})`);
    gradientFill.addColorStop(1, `rgba(${hexToRGB(this.cssValue('line-chart--gradient-fill-end'))}, ${opacity})`);

    return {
      // Datasets configuration options:
      // https://www.chartjs.org/docs/latest/charts/line.html#dataset-properties
      backgroundColor: !this.disableGradientFill ? gradientFill : 'transparent',
      borderCapStyle: 'round',
      borderColor: !this.disableGradientStroke ? gradientStroke : this.cssValue('line-chart--gradient-fill-end'),
      borderWidth: this.lineStroke,
      fill: this.reverseYAxis ? 'start' : !this.disableGradientFill,
      pointStyle: 'circle',
      pointBackgroundColor: this.cssValue('line-chart-point--background-color'),
      pointBorderColor: this.cssValue('line-chart-point--border-color'),
      pointBorderWidth: this.cssValue2Int('line-chart-point--border-width'),
      pointHoverBackgroundColor: this.cssValue('line-chart-point--hover-background-color'),
      pointHoverBorderColor: this.cssValue('line-chart-point--hover-border-color'),
      pointHoverBorderWidth: this.cssValue2Int('line-chart-point--hover-border-width'),
      pointRadius:
        this.markerPoint === LineChartMarkerPointOptions[1] ? 0 : this.cssValue2Int('line-chart-point--border-radius'),
      pointHoverRadius:
        this.markerPoint === LineChartMarkerPointOptions[1]
          ? 0
          : this.cssValue2Int('line-chart-point--hover-border-radius'),
    };
  };

  private scalesCommonConfig = () => ({
    // Grid line configuration options
    // https://www.chartjs.org/docs/latest/axes/styling.html#grid-line-configuration
    color: this.cssValue('line-chart--grid-color'),
    font: { family: this.cssValue('line-chart--font-family') },
    lineWidth: this.gridLineWidth,
    tickWidth: this.gridTickWidth,
  });

  private scalesTicksCommonConfig = () => ({
    // Tick configuration options
    // https://www.chartjs.org/docs/latest/axes/styling.html#tick-configuration
    color: this.cssValue('line-chart-tick--color'),
    display: true,
    font: { size: this.cssValue2Int('line-chart-tick--font-size') },
    padding: this.cssValue2Int('line-chart-tick--padding'),
  });

  private get borderConfig() {
    return {
      border: {
        dash: [2, 4],
        width: this.gridBorderWidth,
      },
    };
  }

  private cssValue = (cssVar: string) => getCSSVariableValue(cssVar, this.hostShadowRoot);

  private cssValue2Int = (cssVar: string) => parseInt(getCSSVariableValue(cssVar, this.hostShadowRoot), 10);

  // render() function
  // Always the last one in the class.
  // ===================================

  render() {
    const style = {
      ...(this.height && { '--line-chart--height': `${this.height}px` }),
    };

    return (
      <Host style={style} role="img">
        <div class="block h-[--line-chart--height] w-full leading-none" part="base">
          <div class="relative h-[--line-chart--height] w-full" part="canvas-outline">
            <canvas
              aria-label={this.ariaLabel}
              id={this.lineChartId}
              ref={(canvas: HTMLCanvasElement) => (this.canvasElemRef = canvas)}
              part="canvas"
              role="img"
            >
              <p class="text-center font-regular text-m text-text-03">
                Your browser does not support the canvas element.
              </p>
            </canvas>
          </div>
        </div>
      </Host>
    );
  }
}
